// Ship 7j — Case Context field catalogs.
//
// Feedback #744 (Gabe, new intake staff): "please add place for judgment,
// what kind of case/judgment, what type of action has been filed against
// them, what agencies are involved, etc. Bc i am new, i would like more
// areas to fill in so i know what questions to ask."
//
// Single source of truth for the "Case Context" section in QuickIntakeModal +
// the read-only display on the lead drawer.  Each entry carries:
//   • value    — enum-safe API key (matches backend CHECK constraint)
//   • label    — human-readable label for radios + chip render
//   • tooltip  — the specific QUESTION intake staff should ask the caller.
//                 Rendered as visible italic helper text (accessible + always
//                 discoverable for a new staffer, not hover-only).
//
// Enum values MUST match the backend CHECK constraints on `leads.judgment_status`
// and `leads.action_type` (Cluster A schema).  Adding options requires a
// coordinated migration + backend + frontend change.

export const JUDGMENT_STATUS_OPTIONS = [
  {
    value: 'none',
    label: 'None',
    tooltip: 'Ask: "Is there already a judgment entered against you?" — pick None if no judgment exists yet.',
  },
  {
    value: 'pending',
    label: 'Pending',
    tooltip: 'Ask: "Is a judgment being sought? Has it been filed with the court but not yet entered?" — pick Pending for in-progress judgments.',
  },
  {
    value: 'entered',
    label: 'Entered',
    tooltip: 'Ask: "Has a judgment already been entered against you? When was it entered?" — pick Entered when a judgment has been issued.',
  },
  {
    value: 'unknown',
    label: 'Unknown',
    tooltip: 'Ask the client, but if they aren\'t sure, pick Unknown. Follow up later once documents are reviewed.',
  },
  {
    value: 'n_a',
    label: 'N/A',
    tooltip: 'Pick N/A when the intake isn\'t about a judgment-based matter at all (e.g. estate planning, contract review).',
  },
]

export const ACTION_TYPE_OPTIONS = [
  {
    value: 'civil_plaintiff',
    label: 'Civil (Plaintiff)',
    tooltip: 'Ask: "Are you the one suing? Have you filed a lawsuit against someone?" — pick Civil (Plaintiff) if the client is the plaintiff.',
  },
  {
    value: 'civil_defendant',
    label: 'Civil (Defendant)',
    tooltip: 'Ask: "Have you been sued? Did you receive a summons or complaint?" — pick Civil (Defendant) if the client is being sued.',
  },
  {
    value: 'criminal_charges',
    label: 'Criminal Charges',
    tooltip: 'Ask: "Have you been charged with a crime? Do you have a court date, arraignment, or arrest record?" — pick Criminal Charges for criminal matters.',
  },
  {
    value: 'admin_agency',
    label: 'Admin / Agency Action',
    tooltip: 'Ask: "Have you received a notice, letter, or order from a government agency (Sheriff, DCFS, IRS, DMV, etc.)?" — pick Admin/Agency for regulatory or agency matters.',
  },
  {
    value: 'none',
    label: 'None',
    tooltip: 'Pick None when there is no pending action — the client is calling proactively (planning, review, general advice).',
  },
  {
    value: 'other',
    label: 'Other',
    tooltip: 'Pick Other for anything not covered above.  Explain in the details field.',
  },
  {
    value: 'unknown',
    label: 'Unknown',
    tooltip: 'Pick Unknown if the client can\'t articulate what kind of action is involved.  Follow up later.',
  },
]

// Common agencies for the tags datalist.  Not enum-restricted — this is a
// TEXT[] column on the backend; these are just suggestions to speed data
// entry.  Free-text agencies are also accepted (e.g. "Board of Prison
// Hearings" or a specific city police department).
export const COMMON_AGENCIES = [
  'Sheriff',
  'Police',
  'Highway Patrol',
  'County of Los Angeles',
  'County of San Diego',
  'County of Orange',
  'County of Riverside',
  'County of San Bernardino',
  'County of Ventura',
  'DCFS',
  'Probation Department',
  'School District',
  'DOJ',
  'FBI',
  'IRS',
  'DMV',
  'ICE',
  'CPS',
  'Adult Protective Services',
  'Board of Prison Hearings',
  'Department of Corrections',
  'Social Security Administration',
  'EDD',
]

// Backend caps agencies_involved at 20 entries via DB CHECK.  Frontend
// enforces the same cap client-side so the user sees the friendly warning
// before the server 400s.
export const AGENCIES_MAX = 20

// Backend caps judgment_details + action_details at 2000 chars via DB
// CHECK.  Mirror client-side so the counter + input maxLength stay in sync.
export const DETAILS_MAX_CHARS = 2000

// Lookup helper — resolves the label for a stored enum value.  Used by the
// lead drawer's read-only render.  Returns the enum value itself as a
// fallback so an unknown/legacy value still renders something meaningful
// (defensive against a future backend enum widening that ships before the
// frontend catches up).
export function labelForJudgmentStatus (value) {
  if (!value) return null
  const hit = JUDGMENT_STATUS_OPTIONS.find(o => o.value === value)
  return hit ? hit.label : value
}

export function labelForActionType (value) {
  if (!value) return null
  const hit = ACTION_TYPE_OPTIONS.find(o => o.value === value)
  return hit ? hit.label : value
}
